import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notificationsKeys, useGetNotificationUnreadCountsQuery, useGetNotificationsStreamSubscription, useInvalidateNotificationUnreadCounts, useReadNotificationsMutation, type NotificationStreamPayload } from '@cntxt/data-access/notifications';
import { isErrorResponse } from '@cntxt/shared/infrastructure/api-component';
import { useIsSigningOut, useSession } from '@cntxt/shared/infrastructure/session';
import { useFeatureFlag } from '../../../../access-control/feature-flags/hooks/useFeatureFlag';
import { getNotificationSseClientEnvOverride } from '../getNotificationSseClientEnvOverride';
const NOTIFICATION_UNREAD_COUNTS_POLL_MS = 60_000;
type UnreadCounts = {
  total: number;
  byModule: Record<string, number>;
};
type NotificationContextType = {
  markNotificationsAsRead: (messageIds: string[]) => void;
  /** Unread across every module, for the bell badge. */
  unreadCount: number;
  /** Unread per module, keyed by processIdBpmn, for the tab badges. */
  unreadByModule: Record<string, number>;
  loading: boolean;
};
const NotificationContext = React.createContext<NotificationContextType>({
  markNotificationsAsRead: () => {
    // Default implementation - will be overridden by provider
  },
  unreadCount: 0,
  unreadByModule: {},
  loading: false
});
const NotificationProvider = t0 => {
  const $ = _c(23);
  const {
    children
  } = t0;
  const queryClient = useQueryClient();
  const {
    status
  } = useSession();
  const isSigningOut = useIsSigningOut();
  const isAuthenticated = status === "authenticated";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getNotificationSseClientEnvOverride();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const notificationSseEnvOverride = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      defaultValue: true
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    enabled: notificationSseEnabledFromFlipt,
    isLoading: isNotificationSseFlagLoading
  } = useFeatureFlag("notification-sse", t2);
  const notificationSseEnabled = notificationSseEnvOverride ?? notificationSseEnabledFromFlipt;
  const isNotificationSseFlagResolved = isAuthenticated && (notificationSseEnvOverride !== null || !isNotificationSseFlagLoading);
  const isNotificationSseReady = isNotificationSseFlagResolved && notificationSseEnabled;
  const shouldPollUnreadCounts = isNotificationSseFlagResolved && !notificationSseEnabled;
  const invalidateUnreadCounts = useInvalidateNotificationUnreadCounts();
  let t3;
  if ($[2] !== queryClient) {
    t3 = payload => {
      queryClient.setQueryData(notificationsKeys.notificationUnreadCounts(), payload.counts);
    };
    $[2] = queryClient;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleNotificationStreamMessage = t3;
  let t4;
  if ($[4] !== handleNotificationStreamMessage || $[5] !== isNotificationSseReady) {
    t4 = {
      enabled: isNotificationSseReady,
      onMessage: handleNotificationStreamMessage
    };
    $[4] = handleNotificationStreamMessage;
    $[5] = isNotificationSseReady;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useGetNotificationsStreamSubscription(t4);
  const shouldPollCounts = status === "authenticated" && !isSigningOut;
  const t5 = shouldPollUnreadCounts ? NOTIFICATION_UNREAD_COUNTS_POLL_MS : false;
  let t6;
  if ($[7] !== shouldPollCounts || $[8] !== t5) {
    t6 = {
      enabled: shouldPollCounts,
      refetchInterval: t5
    };
    $[7] = shouldPollCounts;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data,
    isLoading
  } = useGetNotificationUnreadCountsQuery(t6);
  const {
    mutateAsync
  } = useReadNotificationsMutation();
  let t7;
  if ($[10] !== data) {
    t7 = !data || isErrorResponse(data) ? {
      total: 0,
      byModule: {}
    } : data as UnreadCounts;
    $[10] = data;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const counts = t7;
  let t8;
  if ($[12] !== invalidateUnreadCounts || $[13] !== mutateAsync) {
    t8 = async messageIds => {
      await mutateAsync({
        data: {
          ids: messageIds
        }
      });
      invalidateUnreadCounts();
    };
    $[12] = invalidateUnreadCounts;
    $[13] = mutateAsync;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const markNotificationsAsRead = t8;
  const t9 = isLoading && shouldPollCounts;
  let t10;
  if ($[15] !== counts.byModule || $[16] !== counts.total || $[17] !== markNotificationsAsRead || $[18] !== t9) {
    t10 = {
      unreadCount: counts.total,
      unreadByModule: counts.byModule,
      markNotificationsAsRead,
      loading: t9
    };
    $[15] = counts.byModule;
    $[16] = counts.total;
    $[17] = markNotificationsAsRead;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const contextValue = t10;
  let t11;
  if ($[20] !== children || $[21] !== contextValue) {
    t11 = <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>;
    $[20] = children;
    $[21] = contextValue;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
};
const useNotification = () => {
  return React.useContext(NotificationContext);
};
export { useNotification, NotificationProvider };